import { View, useWindowDimensions, TouchableOpacity, Keyboard, Pressable, StyleSheet } from 'react-native'
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { ThemeConsumer } from '@rneui/themed'
import { useFormik } from 'formik'
import Popover from 'react-native-popover-view'
import * as Yup from "yup"

import CommonDialogBox from '../../../shared/commonComponents/commonDialogBox'
import { CustomModal } from '../../../shared/commonComponents/customModal'
import { config } from '../../../shared/environment'

import { ZenButton, ZenDropdown, ZenIcon, ZenInput, ZenLoader, ZenText, ZenToast } from 'zen-core-elements'
import { ItemTypeValue } from 'zen-core-elements/src/elements/zenDropdown/dropdownPicker'

import { useKeyborad } from '../../../shared/hooks/useKeyborad'
import { getScreenPermission, useAppDispatch, useAppSelector } from '../../../redux/hooks'

import { modifierActionData, modifierFormikEdit, ModifierOption, postModifierset, postModifierSetProps, modifierPayload } from './modifierModule'
import { createModifierOption, createModifierSet, getOneModifierSet, updateModifierOption, updateModifierSet } from '../../../redux/slices/modifierSlice'
import { checkDuplicatesRedux } from '../../../redux/slices/commonSlice'
import { CheckDuplicatesProps } from '../../customerGroups/customerGroupModule'
import { shallowEqual } from 'react-redux'

const AddEditModifier = ({ setModalVisible, setPayload, modalVisible, payload, page }: {
	modalVisible: boolean
	setModalVisible: Dispatch<SetStateAction<boolean>>
	payload?: number | ModifierOption
	page: "set" | "options"
	setPayload: Dispatch<SetStateAction<modifierPayload["payload"] | undefined>>
}) => {
	const { isWrite: isModifierWrite } = useAppSelector(getScreenPermission("ModifierScreen"), shallowEqual)

	const { width } = useWindowDimensions()
	const isKeyboardOpen = useKeyborad()
	const dispatch = useAppDispatch()
	const { value: options } = useAppSelector((state) => state?.modifier?.modifiers?.option)
	const [loader, setLoader] = useState(true)
	const [modifierset, setModifierSet] = useState<modifierActionData>()
	const [dropdownOpen, setDropDownOpen] = useState(false)
	const [checkExistLoader, setCheckExistLoader] = useState(false)
	const [leavePopoverVisible, setLeavePopoverVisible] = useState(false)
	const [transparentLoader, setTransparentLoader] = useState(false)

	useEffect(() => {
		if (modalVisible) {
			if (page == "set" && payload) {
				getOneModifierSetApi()
			} else {
				setLoader(false)
			}
		}
	}, [page, payload, modalVisible])

	const modifierDropdown: ItemTypeValue[] = useMemo(() => {
		if (!options?.length) return []
		return options.map((option) => ({ value: option?.id, label: option?.optionValue }))
	}, [options])

	const ModifierSchema = Yup.object().shape({
		name: Yup.string()
			.matches(/^(?!\s)(?!.*\s$).*\S+.*$/, "Name cannot be empty or spaces only")
			.required('Name is required')
			.max(30, 'Name should not exceed 30 characters.')
			.when("isExistName", {
				is: true,
				then: (schema) =>
					schema.test(
						"unique-name",
						"This name already exists",
						() => false
					),
				otherwise: (schema) => schema,
			}),
		alternateName: Yup.string()
			.max(30, 'Alternate name should not exceed 30 characters')
			.matches(/^(?!\s)(?!.*\s$).*\S+.*$/, "Name cannot be empty or spaces only")
			.when('page', {
				is: 'set',
				then: (s) => s.notRequired(),
				otherwise: (s) => s.notRequired(),
			}),

		price: Yup.string()
			.matches(/^\d+(\.\d{0,2})?$/, 'Only numbers allowed, max two decimals')
			.test('max-value', 'Value should not exceed 9,99,999', (value) => {
				if (!value) return true;
				return parseFloat(value) <= 999999;
			})
			.matches(/^\d{1,6}(\.\d{1,2})?$/, 'Invalid price format')
			.when('page', {
				is: 'options',
				then: (s) => s.required('Price is required'),
				otherwise: (s) => s.notRequired(),
			}),

		modifiers: Yup.array()
			.of(Yup.number().nullable())
			.when('page', {
				is: 'set',
				then: (s) =>
					s
						.min(1, 'Please select at least one modifier option!')
						.required('Please select at least one modifier option!'),
				otherwise: (s) => s.strip()
			}),
	});

	const initialValues = useMemo(() => ({
		page,
		isExistName: false,
		...(page == "set" ? {
			name: modifierset?.modifiername || "",
			alternateName: modifierset?.alternatename || "",
			modifiers: modifierset?.modifiersetmappings?.map((item) => item?.modifierControlId) || []
		} : {
			name: (payload as ModifierOption)?.optionValue || "",
			price: (payload as ModifierOption)?.price?.toString() || ""
		})
	}), [page, modifierset, payload])

	const formik = useFormik({
		initialValues,
		validationSchema: ModifierSchema,
		validateOnChange: true,
		enableReinitialize: true,
		onSubmit: (values: modifierFormikEdit) => {
			Keyboard.dismiss()
			setDropDownOpen(false)
			setTransparentLoader(true)
			proceedApi()
		}
	})

	const selectedModifiersText = useMemo(() => {
		const selectedIds = formik?.values?.modifiers
		if (!selectedIds || !selectedIds.length || !modifierDropdown.length) {
			return "Select a modifier"
		}
		const selectedLabels = modifierDropdown
			.filter((item) => selectedIds.includes(item.value as number))
			.map((item) => item.label)
			.filter(Boolean)

		if (!selectedLabels.length) return "Select a modifier"
		if (selectedLabels.length > 2) {
			return `${selectedLabels.slice(0, 2).join(', ')}... (+${selectedLabels.length - 2})`
		}
		return selectedLabels.join(', ')
	}, [formik?.values?.modifiers, modifierDropdown])

	useEffect(() => {
		if (formik?.values?.name && formik?.initialValues?.name?.trim() !== formik?.values?.name?.trim()) {
			setCheckExistLoader(true)
			const debounce = setTimeout(() => {
				checkDuplicateName()
			}, 1000);

			return () => {
				clearTimeout(debounce)
			}
		} else {
			setCheckExistLoader(false)
		}
	}, [formik?.values?.name])

	const getOneModifierSetApi = () => {
		dispatch(getOneModifierSet({ modifierId: (payload as number), storeId: config?.storeId })).unwrap().then((response) => {
			if (response?.modifierSet?.id) {
				setModifierSet(response?.modifierSet)
			}
			else {
				ZenToast({
					type: "warning",
					message: "Something went wrong, Please try again later"
				})
				clearAll()
			}
		}).catch(() => {
			ZenToast({
				type: "error",
				message: "Failed to get modifier details"
			})
			clearAll()
		}).finally(() => {
			setLoader(false)
		})
	}

	const convertModifierForPayload = () => {
		let result: postModifierSetProps = []
		const uniqueIds = new Set(formik?.values?.modifiers!!)
		options?.forEach((val, index) => {
			if (uniqueIds.has(val?.id)) {
				result.push({
					sortOrder: index + 1,
					storeId: config?.storeId,
					modifierControlId: val?.id,
					optionValue: val?.optionValue,
					price: val?.price
				})
			}
		})
		return result
	}

	const proceedApi = () => {
		setTransparentLoader(true);
		const isOption = page === "options";
		const isUpdate = payload == undefined ? false : true;
		const commonData = {
			name: formik?.values?.name,
			alternateName: formik?.values?.alternateName || "",
			modifiers: isOption ? [] : convertModifierForPayload(),
			price: formik?.values?.price!!,
			modifierId: payload ? (isOption ? (payload as ModifierOption)?.id : payload as number) : undefined
		};

		let action;

		if (isOption) {
			action = isUpdate
				? updateModifierOption({
					data: {
						optionValue: commonData.name,
						price: commonData.price,
						medium: "POS"
					},
					modifierId: commonData?.modifierId!!,
					storeId: config?.storeId
				})
				: createModifierOption({
					data: {
						optionValue: commonData.name,
						price: commonData.price,
						medium: "POS"
					},
					storeId: config?.storeId
				});
		} else {
			action = isUpdate
				? updateModifierSet({
					modifierId: commonData?.modifierId!!,
					data: {
						modifierName: commonData.name,
						alternateName: commonData.alternateName,
						modifiers: commonData.modifiers,
						isDeleted: false,
						medium: "POS"
					},
					storeId: config?.storeId
				})
				: createModifierSet({
					data: {
						modifierName: commonData.name,
						alternateName: commonData.alternateName,
						modifiers: commonData.modifiers,
						isDeleted: false,
						medium: "POS"
					},
					storeId: config?.storeId
				});
		}

		dispatch(action as any)
			.unwrap()
			.then((response: postModifierset) => {
				if (response?.success) {
					ZenToast({
						type: "success",
						message: `Modifier ${isUpdate ? "updated" : "created"} successfully`
					});
					clearAll();
					setPayload((prev) => ({ ...prev!, offset: 0 }));
				} else {
					ZenToast({
						type: "error",
						message: `Failed to ${isUpdate ? "update" : "create"} the modifier, Please contact admin`
					});
				}
			})
			.catch(() => {
				ZenToast({
					type: "error",
					message: `Failed to ${isUpdate ? "update" : "create"} the modifier`
				});
			})
			.finally(() => {
				setTransparentLoader(false);
			});
	};


	const checkDuplicateName = () => {
		const data: CheckDuplicatesProps = {
			value: formik?.values?.name,
			...page == "options" ? {
				tableName: "modifiers",
				columnName: "optionValue",
				id: payload ? (payload as ModifierOption)?.id : null
			} : {
				tableName: "modifierSet",
				columnName: "modifierName",
				id: payload ? (payload as number) : null
			},
			storeId: true,
		}
		dispatch(checkDuplicatesRedux({ storeId: config?.storeId, data })).unwrap().then((response) => {
			if (response?.result?.alreadyExists) {
				formik.setFieldTouched("name", true)
				formik.setFieldValue("isExistName", true)
			} else {
				formik.setFieldValue("isExistName", false)
			}
			setCheckExistLoader(false)
		}).catch(() => {
			ZenToast({
				type: "error",
				message: "Failed to check the duplicate name"
			})
		})
	}

	const clearAll = () => {
		formik.resetForm()
		setModifierSet(undefined)
		setDropDownOpen(false)
		setModalVisible(false)
	}

	//method for popover confirmation 
	const confirmation = (item: { id: number }) => {
		setLeavePopoverVisible(false)
		if (item?.id == 2) {
			clearAll()
		}
	}

	const checkLeave = () => {
		Keyboard?.dismiss();
		if (formik?.dirty)
			setLeavePopoverVisible(true)
		else
			clearAll()
	}

	return (
		<ThemeConsumer>
			{({ theme }) => (
				<CustomModal keyboardView visible={modalVisible} onRequestClose={checkLeave} animationType="slide" transparent={true} statusBarTranslucent>
					<View style={[theme?.modifierSetStyle?.modalOuterView]}>
						<Pressable style={[StyleSheet?.absoluteFill, { backgroundColor: "transparent" }]} onPress={() => { isKeyboardOpen ? Keyboard?.dismiss() : checkLeave() }} />
						<View style={[{ width: width > 900 ? "40%" : width > 600 ? "60%" : "85%" }, theme?.modifierSetStyle?.innerView,]} onStartShouldSetResponder={() => {
							setDropDownOpen(false)
							return false
						}}>
							<View style={theme?.modifierSetStyle?.modelHeader}>
								<ZenText content={payload ? `Edit Modifier ${page}` : `Add Modifier ${page}`} bold={true} size={width > 600 ? theme?.mediumFontSize : theme?.smallerFontSize} color={theme?.dark} />
								<TouchableOpacity onPress={checkLeave}>
									<ZenIcon name='close-circle' type='material-community' size={width > 600 ? 24 : 22} color={theme?.iconColor} />
								</TouchableOpacity>
							</View>
							<View style={[theme?.sellScreenCheckoutStyle?.modalInnerView, { paddingHorizontal: width > 600 ? 20 : 0, paddingTop: width > 600 ? 20 : 10 }]}>
								<View style={{ paddingBottom: 15 }}>
									{loader ?
										<View style={{ height: 200 }}>
											<ZenLoader viewStyle={{ backgroundColor: "transparent", }} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
										</View>
										:
										<View>
											<ZenInput
												disabled={!isModifierWrite}
												label={(page == "options" ? "Option Name" : "Set Name") + "*"}
												value={formik?.values?.name}
												onChangeText={(text: string) => formik.setFieldValue("name", text)}
												labelStyle={theme?.addEditCustomerStyle?.labelStyle}
												inputStyle={[theme?.modifierSetStyle?.inputStyle, { top: -4 }]}
												type="outlined"
												autoCapitalize='none'
												onBlur={() => {
													formik.setFieldTouched("name", true)
													formik.setFieldValue("name", formik?.values?.name?.trim())
												}}
												inputContainerStyle={{ paddingHorizontal: 8 }}
												errorMessage={formik?.touched?.name && formik?.errors.name ? formik?.errors.name : null}
												errorStyle={{ fontFamily: theme.medium, color: theme.red, fontSize: theme.errorFontSize, paddingVertical: formik?.touched?.name && formik?.errors.name ? 5 : 0 }}
											/>
											{page == "set" ? <ZenInput
												label={"Alternate Name"}
												disabled={!isModifierWrite}
												onChangeText={(text: string) => formik.setFieldValue("alternateName", text)}
												value={formik?.values?.alternateName}
												labelStyle={theme?.addEditCustomerStyle?.labelStyle}
												inputStyle={[theme?.modifierSetStyle?.inputStyle, { top: -4 }]}
												type="outlined"
												onBlur={() => {
													formik.setFieldValue("alternateName", formik?.values?.name?.trim())
												}}
												autoCapitalize='none'
												inputContainerStyle={{ paddingHorizontal: 8 }}
												errorMessage={formik?.errors.alternateName ? formik?.errors.alternateName : null}
												errorStyle={{ fontFamily: theme.medium, color: theme.red, fontSize: theme.errorFontSize, paddingVertical: formik?.errors.alternateName ? 5 : 0 }}
											/> : <></>}
											{page == "options" ? <ZenInput
												label={"Price*"}
												disabled={!isModifierWrite}
												onChangeText={(text: string) => formik.setFieldValue("price", text)}
												disabledInputStyle={{ opacity: 1 }}
												value={formik?.values?.price}
												labelStyle={theme?.addEditCustomerStyle?.labelStyle}
												inputStyle={[theme?.modifierSetStyle?.inputStyle, { top: -4 }]}
												type="outlined"
												onBlur={() => {
													formik.setFieldTouched("price", true)
												}}
												keyboardType='numeric'
												inputContainerStyle={{ paddingHorizontal: 8 }}
												errorMessage={formik?.touched?.price && formik?.errors.price ? formik?.errors.price : null}
												errorStyle={{ fontFamily: theme.medium, color: theme.red, fontSize: theme.errorFontSize, paddingVertical: formik?.touched?.price && formik?.errors.price ? 5 : 0 }}
											/> : <></>}
											{page == "set" ?
												<View style={{ marginVertical: 15, marginTop: 0, marginBottom: 15 }}>
													<ZenDropdown
														title='Modifier option*'
														titleStyle={theme?.addEditCustomerStyle?.labelStyle}
														items={modifierDropdown}
														disabled={!isModifierWrite}
														open={dropdownOpen}
														setOpen={setDropDownOpen}
														onPress={() => {
															Keyboard.dismiss()
														}}
														onOpen={() => {
															formik.setFieldTouched("modifiers", true)
														}}
														multiple
														multipleText={selectedModifiersText}
														placeholder="Select a modifier"
														autoScroll={true}
														setValue={() => { }}
														value={formik?.values?.modifiers || []}
														onSelectItem={(item: any) => {
															const selectedValues: any[] = Array.isArray(item) ? item.map((val: any) => val?.value) : []
															const currentValues: any[] = formik?.values?.modifiers || []
															const isSame =
																selectedValues.length === currentValues.length &&
																selectedValues.every((val) => currentValues.includes(val))

															if (!isSame) {
																formik.setFieldValue("modifiers", selectedValues)
															}
														}}
														maxHeight={150}
														dropDownContainerStyle={{ position: "relative", top: 0 }}
														searchable={true}
														listItemLabelStyle={{ fontSize: theme?.smallerFontSize }}
														labelStyle={{ fontSize: theme?.smallerFontSize, color: theme?.dark, fontFamily: theme?.medium }}
														placeholderStyle={{ fontSize: theme?.smallFontSize, color: theme?.labelColor, fontFamily: theme?.medium }}
													/>
													{formik?.touched.modifiers && formik?.errors?.modifiers &&
														<ZenText content={formik?.errors?.modifiers} size={theme?.errorFontSize} color={theme?.red} style={{ paddingTop: 5, marginBottom: -5 }} />}
												</View> : <></>}
										</View>
									}
								</View>
							</View>
							{!loader && isModifierWrite ? <View style={{ alignItems: "flex-end", paddingHorizontal: 15, paddingBottom: 15, }}>
								<ZenButton
									title="Save"
									onPress={() => { formik?.handleSubmit() }}
									disabled={(formik?.dirty && formik?.isValid) ? false : true}
									containerStyle={{ borderRadius: 10 }}
									titleStyle={{ fontSize: theme?.smallerFontSize, paddingHorizontal: 7, top: -1 }}
									buttonStyle={{ borderRadius: 10, backgroundColor: theme?.primary }}
									loading={checkExistLoader}
									loadingStyle={{ paddingHorizontal: 15 }}
								/>
							</View> : <></>}
						</View>
					</View>
					{/* For leave confirmation */}
					<Popover isVisible={leavePopoverVisible} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}>
						<CommonDialogBox type="EDIT" replaceValue="page" confirmation={confirmation} />
					</Popover>
					<CustomModal transparent visible={transparentLoader}>
						<ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={theme?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
					</CustomModal>
				</CustomModal>
			)}
		</ThemeConsumer>
	)
}

export default AddEditModifier